/**
 * Small LRU + TTL cache for search results. Used by the header quick-search
 * so backspacing and retyping the same characters (a very common typing
 * pattern) resolves instantly instead of re-hitting the API.
 */
export class SearchResultCache<T> {
    private readonly cache = new Map<string, { value: T; expiresAt: number }>();

    constructor(
        private readonly maxEntries: number = 20,
        private readonly ttlMs: number = 30_000,
    ) { }

    get(key: string): T | undefined {
        const entry = this.cache.get(key);
        if (!entry) return undefined;

        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return undefined;
        }

        // Re-insert so the key is treated as most-recently-used.
        this.cache.delete(key);
        this.cache.set(key, entry);
        return entry.value;
    }

    set(key: string, value: T): void {
        this.cache.delete(key);
        this.cache.set(key, { value, expiresAt: Date.now() + this.ttlMs });

        while (this.cache.size > this.maxEntries) {
            const oldestKey = this.cache.keys().next().value;
            if (oldestKey === undefined) break;
            this.cache.delete(oldestKey);
        }
    }

    clear(): void {
        this.cache.clear();
    }

    get size(): number {
        return this.cache.size;
    }
}
